
    import {defineComponent, ref, computed, watch, nextTick} from 'vue';
    import debounce from 'lodash/debounce';
    import VueSelect from 'vue-select';
    import {string, array, bool, object} from 'vue-types';

    export default defineComponent({
        name: 'Autocomplete',
        components: {
            VueSelect,
        },
        emits: ['update:modelValue', 'focus', 'autocomplete:select', 'autocomplete:clear'],
        props: {
            placeholder: string().def('Paris, île-de-France, France'),
            focusAfterSelect: bool().def(false),
            dataTestId: string(),
            clearSearchAfterSelection: bool().def(false),
            location: bool().def(false),
            noOptionsLabel: string(),
            modelValue: string(),
            label: string().def('name'),
            autocompleteService: object().isRequired,
            invalid: bool(),
            excludeOptions: array<string>(),
        },
        setup(props, {emit}) {
            let loadingFunction: any = null;
            const selected = ref<string | null>(null);
            const query = ref<string | null>(null);
            const options = ref<string[]>([]);
            const debouncedQuery = debounce(search, 350);
            const noOptions = ref<boolean>(false);
            const select = ref<InstanceType<typeof VueSelect> | null>(null);

            const iconName = computed(() => (props.location ? 'location' : 'search'));

            watch(
                () => props.modelValue,
                () => {
                    selected.value = props.modelValue || null;
                },
                {immediate: true},
            );

            function onSearch(search: any, loading: any): void {
                if (search.length > 0) {
                    loading(true);
                    loadingFunction = loading;
                    query.value = search;
                    debouncedQuery();
                } else {
                    options.value = [];
                    query.value = null;
                }
            }

            async function search(): Promise<void> {
                let [data, error] = await props.autocompleteService.search(query.value);
                if (data) {
                    if (props.excludeOptions?.length) {
                        data = data.filter((val: any) => !props.excludeOptions!.includes(val));
                    }
                    loadingFunction(false);
                    options.value = data;
                    noOptions.value = options.value.length === 0;
                } else if (error) {
                    loadingFunction(false);
                    noOptions.value = true;
                }
            }

            function onSearchBlur(): void {
                noOptions.value = false;
            }

            function onFocus(): void {
                emit('focus');
            }

            function clearSelection(): void {
                selected.value = '';
                select.value?.clearSelection();
            }

            function handleSelectValue(element: string): void {
                selected.value = element;
                emit('update:modelValue', selected.value);
                options.value = [];
                if (props.clearSearchAfterSelection) {
                    clearSelection();
                }
            }

            async function handleUpdatedValue(element: string): Promise<void> {
                if (element) {
                    emit('autocomplete:select', element);
                    if (props.focusAfterSelect) {
                        await nextTick();
                        select.value?.searchEl.focus();
                    }
                } else {
                    emit('autocomplete:clear');
                }
            }

            return {
                select,
                selected,
                query,
                options,
                loadingFunction,
                debouncedQuery,
                noOptions,
                iconName,
                onSearch,
                search,
                onSearchBlur,
                onFocus,
                handleSelectValue,
                handleUpdatedValue,
                clearSelection,
            };
        },
    });
